import { useState } from 'react';
import { Button } from '../../components/Button';
import { CardGrid } from '../../components/CardGrid';
import { ItemCard } from '../../components/ItemCard';
import { Input } from '../../components/Input';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { BookingSummaryRow } from '../../composites/BookingSummaryRow';
import {
  BookingColumn,
  BookingList,
  Columns,
  ConfirmationMeta,
  CreateAccountForm,
  Header,
  ManageBooking,
  ManageBookingDescription,
  SectionTitle,
  UpsellGridWrapper,
  UpsellSection,
  VoucherItem,
  VoucherList,
} from './ConfirmationTemplate.styles';
import type { ConfirmationTemplateProps } from './ConfirmationTemplate.types';

export function ConfirmationTemplate({
  heading,
  subheading,
  confirmationEmail,
  confirmationNumber,
  summaryLines = [],
  voucherCodes = [],
  bookingItems,
  createAccount,
  upsell,
  labels,
}: ConfirmationTemplateProps) {
  const [email, setEmail] = useState(createAccount?.email ?? '');

  const handleCreateAccount = (event: React.FormEvent) => {
    event.preventDefault();
    createAccount?.onSubmit(email);
  };

  return (
    <PageSection>
      <Header>
        <PageHeading title={heading} subtitle={subheading} />
        {confirmationEmail && (
          <ConfirmationMeta>
            {labels.sentConfirmationPrefix}
            <strong>{confirmationEmail}</strong>
          </ConfirmationMeta>
        )}
        {confirmationNumber && (
          <ConfirmationMeta>
            {labels.confirmationNumberPrefix}
            <strong>{confirmationNumber}</strong>
          </ConfirmationMeta>
        )}
        {summaryLines.map((line, index) => (
          <ConfirmationMeta key={`summary-${index}`}>{line}</ConfirmationMeta>
        ))}
        {voucherCodes.length > 0 && (
          <VoucherList>
            {voucherCodes.map((code, index) => (
              <VoucherItem key={`voucher-${index}`}>{code}</VoucherItem>
            ))}
          </VoucherList>
        )}
      </Header>

      <Columns>
        <BookingColumn>
          <SectionTitle>{labels.bookingSectionTitle}</SectionTitle>
          <BookingList>
            {bookingItems.map((item) => (
              <BookingSummaryRow key={item.id} {...item} noImageLabel={labels.noImage} />
            ))}
          </BookingList>
        </BookingColumn>

        {createAccount && (
          <ManageBooking>
            <SectionTitle>{labels.manageBookingTitle}</SectionTitle>
            <ManageBookingDescription>{labels.manageBookingDescription}</ManageBookingDescription>
            <CreateAccountForm onSubmit={handleCreateAccount}>
              <Input
                type="email"
                value={email}
                placeholder={labels.emailLabel}
                aria-label={labels.emailLabel}
                autoComplete="email"
                required
                onChange={(event) => setEmail(event.target.value)}
              />
              <Button type="submit" variant="primary" fullWidth>
                {labels.createAccountCta}
              </Button>
            </CreateAccountForm>
          </ManageBooking>
        )}
      </Columns>

      {upsell && upsell.items.length > 0 && (
        <UpsellSection>
          <PageHeading title={labels.upsellHeading} subtitle={labels.upsellSubheading} />
          <UpsellGridWrapper>
            <CardGrid $columns={3} $mobileColumns={2}>
              {upsell.items.map((item) => (
                <ItemCard
                  key={item.id}
                  image={{
                    src: item.image?.src,
                    alt: item.image?.alt ?? item.title,
                    placeholderLabel: labels.noImage,
                  }}
                  imageAspectRatio="16 / 9"
                  title={item.title}
                  meta={item.description}
                  price={item.price}
                  action={{ label: labels.upsellAddCta, onClick: () => upsell.onAdd(item.id) }}
                />
              ))}
            </CardGrid>
          </UpsellGridWrapper>
        </UpsellSection>
      )}
    </PageSection>
  );
}
